// Servicio de Usuarios (Users)
// Maneja la gestión de usuarios del sistema en Firestore

import { getCollection, getDocument, updateDocument, deleteDocument } from './firestore.service'
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth'
import { doc, setDoc, Timestamp } from 'firebase/firestore'
import { auth, db } from './firebase.config'

const USERS_COLLECTION = 'users'

/**
 * Obtener todos los usuarios
 * @param {Object} options - Opciones de consulta
 * @returns {Promise<Array>} Array de usuarios
 */
export const getAllUsers = async (options = {}) => {
  try {
    const {
      role = null,
      orderByField = 'createdAt',
      orderDirection = 'desc'
    } = options

    const filters = []
    
    if (role) {
      filters.push({ field: 'role', operator: '==', value: role })
    }
    
    return await getCollection(
      USERS_COLLECTION,
      filters,
      orderByField,
      orderDirection
    )
  } catch (error) {
    console.error('Error obteniendo usuarios:', error)
    throw error
  }
}

/**
 * Obtener un usuario por ID
 * @param {string} userId - ID del usuario (UID de Firebase Auth)
 * @returns {Promise<Object|null>} Usuario o null si no existe
 */
export const getUser = async (userId) => {
  try {
    return await getDocument(USERS_COLLECTION, userId)
  } catch (error) {
    console.error('Error obteniendo usuario:', error)
    throw error
  }
}

/**
 * Crear un nuevo usuario
 * Crea el usuario en Firebase Auth y en Firestore
 * @param {Object} userData - Datos del usuario
 * @param {string} userData.email - Email del usuario
 * @param {string} userData.password - Contraseña temporal
 * @param {string} userData.displayName - Nombre del usuario
 * @param {string} userData.role - Rol del usuario (user, admin, superadmin)
 * @returns {Promise<Object>} Usuario creado
 */
export const createUser = async (userData) => {
  try {
    const { email, password, displayName = '', role = 'user' } = userData

    // Crear el usuario en Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(auth, email, password)
    const user = userCredential.user

    // Actualizar el perfil con el nombre si se proporciona
    if (displayName) {
      await updateProfile(user, { displayName })
    }

    // Crear documento del usuario en Firestore
    const userDoc = {
      email: user.email,
      displayName: displayName || '',
      role: role,
      roleId: userData.roleId || null, // ID del rol personalizado (si aplica)
      // Información de contacto
      phone: userData.phone || '',
      phoneAlternate: userData.phoneAlternate || '',
      // Direcciones
      addresses: userData.addresses || [],
      primaryAddressIndex: userData.primaryAddressIndex !== undefined ? userData.primaryAddressIndex : null,
      // Fechas
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      deactivatedAt: null, // Fecha de baja (null si está activo)
      // Auditoría
      emailVerified: user.emailVerified,
      createdBy: auth.currentUser?.uid || null, // ID del admin que creó el usuario
      lastUpdatedBy: auth.currentUser?.uid || null,
      // Notas/observaciones
      notes: userData.notes || ''
    }

    // Guardar en Firestore
    await setDoc(doc(db, USERS_COLLECTION, user.uid), userDoc)

    return {
      id: user.uid,
      ...userDoc
    }
  } catch (error) {
    console.error('Error creando usuario:', error)
    throw error
  }
}

/**
 * Actualizar un usuario
 * @param {string} userId - ID del usuario
 * @param {Object} updates - Datos a actualizar
 * @returns {Promise<void>}
 */
export const updateUser = async (userId, updates) => {
  try {
    await updateDocument(USERS_COLLECTION, userId, updates)
  } catch (error) {
    console.error('Error actualizando usuario:', error)
    throw error
  }
}

/**
 * Actualizar el rol de un usuario
 * @param {string} userId - ID del usuario
 * @param {string} role - Nuevo rol (puede ser fijo o personalizado)
 * @param {string} roleId - ID del rol personalizado (opcional, solo si role es personalizado)
 * @returns {Promise<void>}
 */
export const updateUserRole = async (userId, role, roleId = null) => {
  try {
    const updateData = { role }
    if (roleId) {
      updateData.roleId = roleId
    } else {
      // Si no hay roleId, asegurarse de limpiarlo si existía
      updateData.roleId = null
    }
    await updateDocument(USERS_COLLECTION, userId, updateData)
  } catch (error) {
    console.error('Error actualizando rol del usuario:', error)
    throw error
  }
}

/**
 * Eliminar un usuario
 * NOTA: Esto solo elimina el documento de Firestore, no el usuario de Firebase Auth
 * Para eliminar completamente, se necesitaría Firebase Admin SDK
 * @param {string} userId - ID del usuario
 * @returns {Promise<void>}
 */
export const deleteUser = async (userId) => {
  try {
    await deleteDocument(USERS_COLLECTION, userId)
  } catch (error) {
    console.error('Error eliminando usuario:', error)
    throw error
  }
}

export default {
  getAllUsers,
  getUser,
  createUser,
  updateUser,
  updateUserRole,
  deleteUser
}

